import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { fetchUserByEmail } from '../../../../store/actions/userActions';
import { awaitingCoachVerification } from '../../../auth/ProtectedRoute';
import { Btn, ErrorState, Loading, PageHeader } from '../../../ForBrands/ui/BrandUI';
import { usePhoneTheme } from '../../../ForBrands/useIsPhone';
import { NavIcon } from '../../../ForAthletes/v2/edit/editHubPieces';
import { GROUPS, SECTIONS, nextSection, sectionById } from './coachProfileModel';
import { fetchCoachProfile, shareCoachCode } from './coachProfileServices';
import useAccountForm, { afterLastVisit } from './useAccountForm';
import VerificationCard from './VerificationCard';
import CoachProfilePhone, { PhoneProfileState, phoneTitleFor, useHubReturn, useProfileExit } from './CoachProfilePhone';
import AccountSection from './sections/AccountSection';
import TeamSettingsSection from './sections/TeamSettingsSection';
import AdditionalTeamsSection from './sections/AdditionalTeamsSection';
import SubscriptionSection from './sections/SubscriptionSection';

// Coach Mode "Profile & verification" (/coach/profile), Hub model v2: the
// verification card and the section list on the left, the open section
// (?section=) on the right. On phones (the Coach Mode phone theme, below
// 640px) the hub and each section are separate screens (CoachProfilePhone).
// Both layouts share this loader and one name form, so a phone turning to
// landscape keeps what was typed.

const EMPTY = Object.freeze({});

const SectionNav = ({ current, onOpen }) =>
  GROUPS.map((group) => (
    <div key={group} className="flex flex-col gap-1">
      <div className="pb-1 pl-3">
        <span className="bm-overline !text-bm-tertiary">{group}</span>
      </div>
      {SECTIONS.filter((s) => s.group === group).map((s) => {
        const active = s.id === current;
        return (
          <button
            key={s.id}
            type="button"
            aria-current={active ? 'page' : undefined}
            onClick={() => onOpen(s.id)}
            className={`flex h-[52px] w-full items-center gap-3 rounded-[12px] px-3 py-[10px] text-left ${active ? 'border border-bm-border bg-white' : 'hover:bg-bm-subtle'}`}
          >
            <NavIcon src={s.icon} active={active} />
            <span className={`flex-1 bm-label-l ${active ? 'text-bm-ink' : 'text-bm-muted'}`}>{s.label}</span>
          </button>
        );
      })}
    </div>
  ));

const HubBody = ({ profile, onAccountSaved, phone = false, onLeave }) => {
  const [params, setParams] = useSearchParams();
  const account = useAccountForm(profile.account, onAccountSaved);
  if (phone) return <CoachProfilePhone profile={profile} account={account} params={params} setParams={setParams} onLeave={onLeave} />;

  const current = sectionById(params.get('section'));
  const next = nextSection(current.id);

  const open = (id) => {
    if (account.isDirty()) account.save();
    setParams({ section: id });
    window.scrollTo({ top: 0 });
  };
  const openNext = () => open(next.id);
  // Account's Next waits for the save, so a refused name stays in view.
  const saveThenNext = async () => {
    if ((account.isDirty() || account.status === 'error') && !(await account.save())) return;
    openNext();
  };

  const body = {
    account: <AccountSection account={profile.account} form={account} next={next} onNext={saveThenNext} />,
    team: <TeamSettingsSection team={profile.team} next={next} onNext={openNext} />,
    additional: <AdditionalTeamsSection teams={profile.additionalTeams} leagueAdmin={profile.leagueAdmin} next={next} onNext={openNext} />,
    subscription: <SubscriptionSection />,
  }[current.id];

  return (
    <div className="flex flex-col gap-6 pb-14 pt-[28px] lg:flex-row lg:gap-10">
      <aside className="flex w-full shrink-0 flex-col gap-5 lg:w-[300px]">
        {/* The primary team row names a program code's program and age groups. */}
        <VerificationCard verification={profile.verification} team={profile.team} teamName={profile.team?.name || ''} onSend={shareCoachCode} />
        <SectionNav current={current.id} onOpen={open} />
      </aside>
      <section aria-label={current.label} className="flex w-full min-w-0 flex-col gap-6 lg:w-[720px]">
        {body}
      </section>
    </div>
  );
};

const CoachProfileHub = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user.user || EMPTY);
  const phone = usePhoneTheme();
  const [params] = useSearchParams();
  const leave = useProfileExit(awaitingCoachVerification(user));
  const toHub = useHubReturn();
  const [profile, setProfile] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setFailed(false);
    afterLastVisit()
      .then(() => fetchCoachProfile())
      .then(
        (data) => live && setProfile(data),
        () => live && setFailed(true),
      );
    return () => {
      live = false;
    };
  }, [attempt]);

  // An admin verified (or rejected) the coach since this session loaded the
  // account: refresh it so the shell and the route gate catch up.
  const serverStatus = profile?.verification?.status;
  const storeStatus = user.coachVerification?.status || 'not_submitted';
  useEffect(() => {
    if (serverStatus && serverStatus !== storeStatus && user.email) dispatch(fetchUserByEmail(user.email));
  }, [serverStatus, storeStatus, user.email, dispatch]);

  const onAccountSaved = (account) => {
    setProfile((prev) => (prev ? { ...prev, account } : prev));
    // The topbar shows the name from the account record.
    if (account?.email) dispatch(fetchUserByEmail(account.email));
  };

  const retry = () => setAttempt((n) => n + 1);

  // The loaded hub keeps its place across both layouts (see above).
  let content;
  if (profile) content = <HubBody profile={profile} onAccountSaved={onAccountSaved} phone={phone} onLeave={leave} />;
  else if (phone) {
    // Back over a section's loading or error state goes to the hub (which
    // then loads the same way); over the hub's own, it leaves the profile.
    const section = params.get('section');
    const inSection = SECTIONS.some((s) => s.id === section);
    content = <PhoneProfileState title={phoneTitleFor(section)} onBack={inSection ? toHub : leave} failed={failed} onRetry={retry} />;
  } else if (failed) {
    content = (
      <div className="flex flex-col items-start gap-4 py-10">
        <ErrorState>We could not load your profile.</ErrorState>
        <Btn variant="secondary" onClick={retry}>
          Try again
        </Btn>
      </div>
    );
  } else content = <Loading label="Loading your profile..." />;

  // Phones: the screens bring their own stack header instead of the page head.
  return (
    <div>
      {!phone && <PageHeader eyebrow="Coach Mode" title="Profile & verification" subtitle="Your account details, verification status and team settings." />}
      {content}
    </div>
  );
};

export default CoachProfileHub;
